import {
  // IconMenuFilter,
  IconPen,
  IconPlusRounded,
  IconTrash,
} from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllGeneralSettingCategories,
  getAllGeneralSettings,
  getAllTenantsFilter,
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import styled from "styled-components";
import {
  GeneralSettingConfirmationModal,
  GeneralSettingFormModal,
} from "./components";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const GeneralSettingsContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const { isLoading, isModalFormOpen, isModalConfirmationOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);
  const { generalSettingCategories, generalSettings } = useSelector(
    (state: any) => state.generalSettingReducer
  );
  const {
    currentPage,
    totalPage,
    limitPage,
    lengthPage,
    filterCategoryPage,
    filterTenantPage,
  } = useSelector((state: any) => state.generalSettingReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedGeneralSettingId, setSelectedGeneralSettingId] = useState("");

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllGeneralSettings(
        1,
        event.target.value,
        limitPage,
        filterCategoryPage,
        filterTenantPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllGeneralSettings(
        currentPage,
        "",
        limitPage,
        filterCategoryPage,
        filterTenantPage
      ) as any
    );
  };

  const handleOpenFormModal = (generalSettingId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedGeneralSettingId(generalSettingId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedGeneralSettingId("");
  };

  const handleOpenConfirmModal = (generalSettingId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedGeneralSettingId(generalSettingId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedGeneralSettingId("");
  };

  useEffect(() => {
    dispatch(getAllTenantsFilter() as any);
    dispatch(getAllGeneralSettingCategories() as any);
    dispatch(getAllGeneralSettings() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            General Settings
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"space-between"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="textDummy"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />
            {userRole === "superadmin" ? (
              <FilterKit
                orderByOptions={[
                  {
                    key: tenantsFilter,
                    label: "Tenant",
                    options: tenantsFilter.map((tenant: any) => {
                      return {
                        label: tenant.name,
                        value: tenant.id,
                      };
                    }),
                  },
                ]}
                onChangeOrder={(e: any) => {
                  dispatch(
                    getAllGeneralSettings(
                      1,
                      "",
                      limitPage,
                      filterCategoryPage,
                      e[tenantsFilter] ? e[tenantsFilter].value : ""
                    ) as any
                  );
                }}
              />
            ) : (
              <></>
            )}

            <FilterKit
              orderByOptions={[
                {
                  key: generalSettingCategories,
                  label: "Category",
                  options: generalSettingCategories.map((category: any) => {
                    return {
                      label: toCapitilizeCase(category.category),
                      value:
                        category.category !== "all" ? category.category : "",
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllGeneralSettings(
                    1,
                    "",
                    limitPage,
                    e[generalSettingCategories]
                      ? e[generalSettingCategories].value
                      : "",
                    filterTenantPage
                  ) as any
                );
              }}
            />
          </Box>

          <Stack
            display={"flex"}
            justifyContent={"right"}
            flexDirection={"row"}
          >
            <Button
              variant="primary"
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Add General Setting
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
            <TableHeadRow>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Value</TableHeadCell>
              <TableHeadCell minWidth={200}>Category</TableHeadCell>
              <TableHeadCell>Description</TableHeadCell>
              {userRole === "superadmin" ? (
                <TableHeadCell minWidth={150}>Tenant</TableHeadCell>
              ) : (
                <></>
              )}
              {userRole === "superadmin" ? (
                <TableHeadCell>Action</TableHeadCell>
              ) : (
                <></>
              )}
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton
                rowsNumber={10}
                columnsNumber={userRole === "superadmin" ? 5 : 4}
              />
            ) : !generalSettings.length ? (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={userRole === "superadmin" ? 5 : 4}
                  textAlign={"center"}
                >
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              generalSettings.map((generalSetting: any) => {
                return (
                  <TableBodyRow key={generalSetting.id}>
                    <TableBodyCell>{generalSetting.name}</TableBodyCell>
                    <TableBodyCell>{generalSetting.value}</TableBodyCell>
                    <TableBodyCell>
                      <Badge size="md" variant="default">
                        {toCapitilizeCase(generalSetting.category)}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>{generalSetting.description}</TableBodyCell>
                    {userRole === "superadmin" ? (
                      <TableBodyCell>
                        <Badge size="md" variant="info">
                          {generalSetting.tenant.name}
                        </Badge>
                      </TableBodyCell>
                    ) : (
                      <></>
                    )}

                    <TableBodyCell>
                      <IconButton
                        onClick={() => handleOpenFormModal(generalSetting.id)}
                      >
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          handleOpenConfirmModal(generalSetting.id)
                        }
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllGeneralSettings(
                select,
                searchValue,
                limitPage,
                filterCategoryPage,
                filterTenantPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllGeneralSettings(
                  1,
                  searchValue,
                  selectedItem,
                  filterCategoryPage,
                  filterTenantPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <GeneralSettingFormModal
        generalSettingId={selectedGeneralSettingId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <GeneralSettingConfirmationModal
        generalSettingId={selectedGeneralSettingId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />
    </Box>
  );
};

export default GeneralSettingsContent;
